import InvitationSessionService, {
  type InvitationSession,
} from "./invitation-session-service";

const REQUEST_TIMEOUT_MS = 3000;
const MAX_ATTEMPTS = 3;

function isTimeout(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === "TimeoutError" || error.name === "AbortError")
  );
}

// Checks the fields the flow cannot work without: the signed trio forwarded on
// signup and the email that must match the invitation.
function isInvitationSession(value: unknown): value is InvitationSession {
  const session = value as Partial<InvitationSession> | null;
  return (
    typeof session?.invite_payload === "string" &&
    typeof session.invite_signature === "string" &&
    typeof session.invite_version === "string" &&
    typeof session.email === "string"
  );
}

/**
 * Fetches the invitation session with a plain GET (no custom headers) so the
 * browser sends no CORS preflight; the endpoint has no OPTIONS route.
 *
 * Logs never include the session ID or session data.
 */
export default class DefaultInvitationSessionService implements InvitationSessionService {
  private readonly invitationSessionUrl: string | undefined;

  constructor(invitationSessionUrl: string | undefined) {
    this.invitationSessionUrl = invitationSessionUrl;
  }

  async getSession(sessionId: string): Promise<InvitationSession> {
    if (!this.invitationSessionUrl) {
      throw new Error("Invitation session URL is not configured");
    }

    for (let attempt = 1; ; attempt++) {
      try {
        return await this.fetchSession(sessionId, this.invitationSessionUrl);
      } catch (error) {
        if (!isTimeout(error)) {
          throw error;
        }
        if (attempt === MAX_ATTEMPTS) {
          console.error(
            `[invitation] timed out after ${MAX_ATTEMPTS} attempts`,
          );
          throw new Error("Could not fetch invitation session");
        }
      }
    }
  }

  /** Single request; timeouts are rethrown unchanged so `getSession` can retry them. */
  private async fetchSession(
    sessionId: string,
    invitationSessionUrl: string,
  ): Promise<InvitationSession> {
    const searchParams = new URLSearchParams({ sessionId });

    let response: Response;
    try {
      response = await fetch(`${invitationSessionUrl}?${searchParams}`, {
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
    } catch (error) {
      if (isTimeout(error)) {
        throw error;
      }
      console.error("[invitation] fetch threw (network/CORS)");
      throw new Error("Could not fetch invitation session");
    }

    if (!response.ok) {
      console.error("[invitation] non-OK response", {
        status: response.status,
      });
      throw new Error("Could not fetch invitation session");
    }

    const session: unknown = await response.json();
    if (!isInvitationSession(session)) {
      console.error("[invitation] required session fields missing");
      throw new Error("Invalid invitation session");
    }
    return session;
  }
}
